import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(e.target.files);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Handle file attachments
      let attachmentsText = 'No attachments';
      if (files && files.length > 0) {
        const fileNames = Array.from(files).map(file => file.name).join(', ');
        attachmentsText = `${files.length} file(s): ${fileNames}`;
      }

      // Create URL-encoded form data
      const formBody = new URLSearchParams({
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        attachments: attachmentsText,
      }).toString();

      // Submit to Supabase Edge Function
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/contact-form-handler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: ''
        });
        setFiles(null);
        // Reset file input
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        console.error('Form submission error:', result);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Background Image with Overlay */}
      <div className="fixed inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9d7f508f379785de2c6a904c47e30fab.jpg"
          alt="HVAC Equipment Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B3E]/95 via-[#0D1B3E]/90 to-[#0066FF]/75"></div>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow pt-20 md:pt-24 pb-12 md:pb-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-8 md:mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4">Request a Quote</h1>
              <p className="text-base md:text-lg text-gray-200 px-4">Fill out the form below and we'll get back to you with a customized quote for your industrial needs.</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 md:p-10 lg:p-12">
              <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="Enter your company name"
                  />
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Please describe the products or services you're interested in..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {/* File Upload */}
                <div>
                  <label htmlFor="file-upload" className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Files or Pictures
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="file-upload"
                      name="attachments"
                      onChange={handleFileChange}
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Supported formats: Images, PDF, DOC, DOCX</p>
                  {files && files.length > 0 && (
                    <p className="text-sm text-teal-600 mt-2">
                      <i className="ri-file-line mr-1"></i>
                      {files.length} file(s) selected
                    </p>
                  )}
                </div>

                {/* Submit Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xl mr-3 mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-green-800 mb-1">Quote Request Submitted!</h4>
                      <p className="text-sm text-green-700">Thank you for your request. We'll review your information and get back to you shortly with a customized quote.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <i className="ri-error-warning-fill text-red-500 text-xl mr-3 mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-red-800 mb-1">Submission Failed</h4>
                      <p className="text-sm text-red-700">There was an error submitting your request. Please try again or contact us directly.</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap text-sm md:text-base"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-send-plane-fill mr-2"></i>
                        Submit Quote Request
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Additional Info */}
            <div className="mt-8 md:mt-12 text-center">
              <p className="text-gray-200 mb-4 text-sm md:text-base">Need immediate assistance?</p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 md:gap-6">
                <a href="tel:+18084217414" className="flex items-center justify-center text-white hover:text-gray-200 font-semibold text-sm md:text-base">
                  <i className="ri-phone-fill mr-2"></i>
                  Call Us: +1 (808) 421-7414
                </a>
                <a href="mailto:support@superiorethi.com" className="flex items-center justify-center text-white hover:text-gray-200 font-semibold text-sm md:text-base">
                  <i className="ri-mail-fill mr-2"></i>
                  Email: support@superiorethi.com
                </a>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
